import type { CalendarEvent } from "@ilamy/calendar";
import type { PatchOperation } from "@medplum/core";
import type {
  CodeableConcept,
  Coding,
  Duration,
  Extension,
  Schedule,
} from "@medplum/fhirtypes";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { TFunction } from "i18next";

import type { DayCode } from "@/types/fhir";
import type {
  CalendarView,
  ConsultationBlock,
  ConsultationDurationError,
  HoursItem,
  ParsedParticipantRef,
  ParsedSchedulingParams,
  SchedulingParams,
  SlotBuffer,
  TimeRange,
  WeekSchedule,
} from "@/types/scheduling";
import {
  FHIR_CODES,
  FHIR_EXTENSION_URLS,
  FHIR_EXTENSIONS,
} from "@/constants/fhir.constants";
import {
  CALENDAR_VIEWS,
  CONSULTATION_CODE_PATTERN,
  DAY_CODES,
  DEFAULT_RANGE,
  DEFAULT_WEEK_SCHEDULE,
  MAX_CONSULTATION_DURATION,
  MIN_CONSULTATION_DURATION,
  MIN_SLOT_DURATION,
  SCHEDULING_DISPLAY_TIME_FORMAT,
  SCHEDULING_TIME_FORMAT,
  WORKING_HOURS_OPTIONS,
} from "@/constants/scheduling.constants";
import {
  getExtension,
  getExtensionValue,
  isNullable,
  isString,
} from "@/lib/utils";

const DAY_CODE_TO_DAY_NUMBER: Record<DayCode, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const DAYS_IN_WEEK = 7;

const DAY_NUMBER_TO_DAY_CODE = Object.entries(DAY_CODE_TO_DAY_NUMBER).reduce<
  Record<number, DayCode>
>((acc, [code, number]) => ({ ...acc, [number]: code as DayCode }), {});

/**
 * dayjs `.day()` (0 = Sunday) → the FHIR day code used by WeekSchedule
 */
export const dayCodeForWeekday = (weekday: number): DayCode =>
  DAY_NUMBER_TO_DAY_CODE[weekday];

const TIME_ANCHOR = "2000-01-01";
const END_OF_ANCHOR_DAY = "2000-01-02T00:00";

/**
 * Sub-extension URLs the office-visit block takes from the form — exactly
 * what buildOfficeVisitSubExtensions emits. On patch these are replaced
 * wholesale, while any other sub-extension (e.g. server- or EHR-owned) is
 * preserved. Listed explicitly: `serviceType` shares the URL map but is
 * never ours.
 */
const UI_OWNED_SCHEDULING_URLS = new Set<string>([
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.AVAILABILITY,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.DURATION,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER_LEGACY,
]);

/**
 * What a save rewrites on a consultation block. `duration` stays: it must
 * keep agreeing with the code bookings match on.
 */
const CONSULTATION_OWNED_SCHEDULING_URLS = new Set<string>([
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.AVAILABILITY,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER,
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER_LEGACY,
]);

/**
 * The shared hours. Medplum has no schedule-wide field, so every block
 * stores a copy, and a save writes it to all of them — blocks outside the
 * edit scope included. Their buffer, duration and alignment stay put.
 */
const SHARED_HOURS_SCHEDULING_URLS = new Set<string>([
  FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.AVAILABILITY,
]);

const isSchedulingParameters = ({ url }: Extension) =>
  url === FHIR_EXTENSIONS.SCHEDULING_PARAMETERS;

const getServiceTypeCodings = (block: Extension): Coding[] =>
  getExtensionValue<CodeableConcept>(
    block,
    FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.SERVICE_TYPE
  )?.coding ?? [];

const isOfficeVisitBlock = (block: Extension) =>
  getServiceTypeCodings(block).some(
    ({ code }) => code === FHIR_CODES.OFFICE_VISIT
  );

/**
 * `N` when `code` is `CONSULT-{N}-MIN`, otherwise undefined. The one
 * definition of the page's edit scope: only these blocks are edited, created
 * or removed. `CONSULT-STD`, `CONSULT-15` and any other code are left alone.
 */
export const matchConsultationCode = (
  code: string | undefined
): number | undefined => {
  const match = code?.match(CONSULTATION_CODE_PATTERN);

  return match ? Number(match[1]) : undefined;
};

const toConsultationCode = (duration: number) => `CONSULT-${duration}-MIN`;

/**
 * Stored in English and translated when rendered. AKB's Spanish displays
 * are kept as written.
 */
export const defaultConsultationDisplay = (duration: number) =>
  `Consultation ${duration} minutes`;

/**
 * Read and save share it, so a blank display is the default text on both
 * sides and a block never ends up with a blank title. Any other display is
 * kept exactly as stored: a save must not rewrite text nobody edited.
 */
const resolveConsultationDisplay = (
  display: string | undefined,
  duration: number
) => (display?.trim() ? display : defaultConsultationDisplay(duration));

/**
 * The default text is stored in English, so it renders through one
 * interpolated key — a key per duration would miss custom ones like 40 min.
 * Any other display (AKB's Spanish, a user's text) renders as written.
 */
export const formatConsultationTitle = (
  { display, duration }: ConsultationBlock,
  t: TFunction
): string => {
  const resolved = resolveConsultationDisplay(display, duration);

  return resolved === defaultConsultationDisplay(duration)
    ? t("Consultation {{minutes}} minutes", { minutes: duration })
    : resolved;
};

type ConsultationCoding = {
  code: string;
  display: string | undefined;
  duration: number;
};

const findConsultationCoding = (
  block: Extension
): ConsultationCoding | undefined => {
  for (const { code, display } of getServiceTypeCodings(block)) {
    const duration = matchConsultationCode(code);

    if (code && !isNullable(duration)) {
      return { code, display, duration };
    }
  }

  return undefined;
};

/**
 * The block the form reads from: `office-visit`, the fallback for services
 * without a code. Otherwise the first block — legacy single-block schedules
 * carry no `serviceType`, and the windows and buffer agree across blocks.
 * Returns -1 when the schedule has no SchedulingParameters block.
 */
const findPrimarySchedulingIndex = (extensions: Extension[]): number => {
  const officeVisitIndex = extensions.findIndex(
    (extension) =>
      isSchedulingParameters(extension) && isOfficeVisitBlock(extension)
  );

  return officeVisitIndex >= 0
    ? officeVisitIndex
    : extensions.findIndex(isSchedulingParameters);
};

const isUncodedBlock = (block: Extension) =>
  isSchedulingParameters(block) &&
  !getServiceTypeCodings(block).some(({ code }) => code);

/**
 * The block that also takes the form's slot duration and alignment:
 * `office-visit`, or the only uncoded block of a legacy schedule, even after
 * consultations were added next to it. A coded block's duration and
 * alignment follow its own code, so on a schedule without `office-visit` no
 * coded block takes them, not even a lone one. Returns -1 in that case.
 */
const findSlotDurationOwnerIndex = (extensions: Extension[]): number => {
  const primaryIndex = findPrimarySchedulingIndex(extensions);

  if (primaryIndex < 0) {
    return -1;
  }

  const primaryBlock = extensions[primaryIndex];
  const uncodedBlocks = extensions.filter(isUncodedBlock);
  const isLegacyBlock =
    uncodedBlocks.length === 1 && uncodedBlocks[0] === primaryBlock;

  return isLegacyBlock || isOfficeVisitBlock(primaryBlock) ? primaryIndex : -1;
};

/**
 * A schedule with no block yet is an office visit: its first save adds the
 * office-visit block.
 */
const findHoursItem = (extensions: Extension[]): HoursItem =>
  !extensions.some(isSchedulingParameters) ||
  findSlotDurationOwnerIndex(extensions) >= 0
    ? "office-visit"
    : "working-hours";

export const parseParticipantRef = (ref: string): ParsedParticipantRef => {
  const [resourceType, id = ""] = ref.split("/");

  if (resourceType === "Patient") {
    return { resourceType, id, ref };
  }

  if (resourceType === "PractitionerRole") {
    return { resourceType, id, ref };
  }

  return { resourceType: "Other", id, ref };
};

export const parseScheduleTime = (time: string) =>
  dayjs(`${TIME_ANCHOR}T${time}`);

const parseScheduleEndTime = (end: string) =>
  end === "00:00" ? dayjs(END_OF_ANCHOR_DAY) : parseScheduleTime(end);

export const isDayCode = (value: unknown): value is DayCode =>
  isString(value) && (DAY_CODES as string[]).includes(value);

export const isCalendarView = (value: unknown): value is CalendarView =>
  isString(value) && (CALENDAR_VIEWS as string[]).includes(value);

export const isValidRange = ({ start, end }: TimeRange) =>
  parseScheduleTime(start).isBefore(parseScheduleEndTime(end));

export const formatTimeRange = (
  { start, end }: TimeRange,
  separator: string
): string =>
  `${parseScheduleTime(start).format(SCHEDULING_DISPLAY_TIME_FORMAT)} ${separator} ${parseScheduleTime(end).format(SCHEDULING_DISPLAY_TIME_FORMAT)}`;

/**
 * FHIR `time` carries seconds (`"09:00:00"`); `TimeRange` is `HH:mm`.
 */
export const toRangeTime = (time: string): string => time.slice(0, 5);

/**
 * The reverse of `toRangeTime`: puts the seconds back for the wire.
 */
export const toWireTime = (time: string): string => `${time}:00`;

type TimeOption = { value: string; label: string };

/**
 * Normalises free-form time text typed by the user into a working-hours
 * option. Accepts: exact label/value match against WORKING_HOURS_OPTIONS,
 * any 24-hour time (e.g. "21:30"), and any 12-hour time (e.g. "9:30pm").
 * Returns null when the input cannot be recognised as a valid time.
 */
export const parseTimeInput = (text: string): TimeOption | null => {
  const trimmed = text.trim();

  if (!trimmed) {
    return null;
  }

  const lower = trimmed.toLowerCase();

  const byLabelOrValue = WORKING_HOURS_OPTIONS.find(
    (option) => option.label.toLowerCase() === lower || option.value === trimmed
  );

  if (byLabelOrValue) {
    return byLabelOrValue;
  }

  const match24h = trimmed.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);

  if (match24h) {
    const [, rawHours, rawMinutes] = match24h;
    const time = parseScheduleTime(
      `${rawHours.padStart(2, "0")}:${rawMinutes}`
    );

    return {
      value: time.format(SCHEDULING_TIME_FORMAT),
      label: time.format(SCHEDULING_DISPLAY_TIME_FORMAT),
    };
  }

  const match12h = trimmed.match(
    /^(0?[1-9]|1[012])(?::([0-5]\d))?\s*(am|pm)$/i
  );

  if (match12h) {
    const [, rawHours, rawMinutes, rawPeriod] = match12h;
    let hours = parseInt(rawHours);
    const minutes = rawMinutes ? parseInt(rawMinutes) : 0;
    const period = rawPeriod.toLowerCase();

    if (period === "am") {
      if (hours === 12) {
        hours = 0;
      }
    } else {
      if (hours !== 12) {
        hours += 12;
      }
    }

    const time = parseScheduleTime(
      `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
    );

    return {
      value: time.format(SCHEDULING_TIME_FORMAT),
      label: time.format(SCHEDULING_DISPLAY_TIME_FORMAT),
    };
  }

  return null;
};

const DEFAULT_SLOT_DURATION = 60;
const DEFAULT_BUFFER_MINUTES = 15;
const DEFAULT_AVAILABILITY_DURATION = 8;

/**
 * A fresh, fully disabled week — the starting point for any group-by that
 * fills days in from a flat list of ranges. Built per call rather than shared
 * as a constant: callers mutate the day they are filling.
 */
export const createEmptyWeekSchedule = (): WeekSchedule =>
  DAY_CODES.reduce<WeekSchedule>(
    (acc, day) => ({ ...acc, [day]: { enabled: false, ranges: [] } }),
    {} as WeekSchedule
  );

const buildWeekScheduleFromAvailabilities = (
  availabilities: Extension[]
): WeekSchedule => {
  const schedule = createEmptyWeekSchedule();

  for (const entry of availabilities) {
    const repeat = entry.valueTiming?.repeat;
    if (!repeat) {
      continue;
    }

    const days = repeat.dayOfWeek ?? [];
    const startTime = repeat.timeOfDay?.[0]?.slice(0, 5) ?? DEFAULT_RANGE.start;
    const duration = repeat.duration ?? DEFAULT_AVAILABILITY_DURATION;
    const durationUnit = repeat.durationUnit === "min" ? "minute" : "hour";
    const endTime = parseScheduleTime(startTime)
      .add(duration, durationUnit)
      .format(SCHEDULING_TIME_FORMAT);

    for (const day of days) {
      schedule[day].enabled = true;
      schedule[day].ranges.push({ start: startTime, end: endTime });
    }
  }

  return schedule;
};

const getAvailabilityEntries = (block: Extension): Extension[] =>
  (block.extension ?? []).filter(
    ({ url }) => url === FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.AVAILABILITY
  );

/**
 * A block's stored hours in a form two blocks can be compared by: the same
 * ranges stored in another entry order are the same hours.
 */
const toComparableHours = (block: Extension): string => {
  const weekSchedule = buildWeekScheduleFromAvailabilities(
    getAvailabilityEntries(block)
  );

  return JSON.stringify(
    DAY_CODES.map((day) =>
      weekSchedule[day].ranges.map(({ start, end }) => `${start}-${end}`).sort()
    )
  );
};

/**
 * Blocks outside the edit scope count too: a save writes the shared hours
 * into them as well.
 */
const findHoursDiffer = (extensions: Extension[], primaryIndex: number) => {
  if (primaryIndex < 0) {
    return false;
  }

  const primaryHours = toComparableHours(extensions[primaryIndex]);

  return extensions
    .filter(isSchedulingParameters)
    .some((block) => toComparableHours(block) !== primaryHours);
};

const parseBuffer = (block: Extension): SlotBuffer => {
  const bufferMinutes = (
    getExtension(block, FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER) ??
    getExtension(block, FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER_LEGACY)
  )?.valueDuration?.value;

  return !isNullable(bufferMinutes)
    ? { enabled: true, minutes: bufferMinutes }
    : { enabled: false, minutes: DEFAULT_BUFFER_MINUTES };
};

const parseConsultationBlock = (
  block: Extension,
  extensionIndex: number
): ConsultationBlock | undefined => {
  const consultationCoding = findConsultationCoding(block);

  if (!consultationCoding) {
    return undefined;
  }

  const { display, duration } = consultationCoding;
  const storedAlignment = getExtensionValue<Duration>(
    block,
    FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL
  )?.value;

  return {
    extensionIndex,
    /**
     * The code's `N`, even over a disagreeing `duration` sub-extension:
     * bookings match on the code.
     */
    duration,
    /**
     * Below the minimum the form cannot hold it, and near 0 the calendar
     * would build tens of thousands of slots.
     */
    alignmentInterval:
      storedAlignment !== undefined && storedAlignment >= MIN_SLOT_DURATION
        ? storedAlignment
        : duration,
    buffer: parseBuffer(block),
    display: resolveConsultationDisplay(display, duration),
  };
};

/**
 * By ascending duration. The sort is stable, so two blocks with one code
 * keep their schedule order.
 */
const parseConsultations = (extensions: Extension[]): ConsultationBlock[] =>
  extensions
    .flatMap((block, index) =>
      isSchedulingParameters(block)
        ? (parseConsultationBlock(block, index) ?? [])
        : []
    )
    .sort((first, second) => first.duration - second.duration);

/**
 * A schedule with no block counts the office-visit block its first save
 * adds.
 */
const countOtherBlocks = (extensions: Extension[]): number => {
  const blocks = extensions.filter(isSchedulingParameters);

  return blocks.length
    ? blocks.filter((block) => !findConsultationCoding(block)).length
    : 1;
};

export const parseScheduleAvailability = (
  schedule: Schedule
): ParsedSchedulingParams => {
  const extensions = schedule.extension ?? [];
  const primaryIndex = findPrimarySchedulingIndex(extensions);
  const consultations = parseConsultations(extensions);
  const hoursItem = findHoursItem(extensions);
  const hoursDiffer = findHoursDiffer(extensions, primaryIndex);
  const otherBlockCount = countOtherBlocks(extensions);

  if (primaryIndex < 0) {
    return {
      weekSchedule: DEFAULT_WEEK_SCHEDULE,
      slotDuration: DEFAULT_SLOT_DURATION,
      buffer: { enabled: false, minutes: DEFAULT_BUFFER_MINUTES },
      consultations,
      hoursItem,
      hoursDiffer,
      otherBlockCount,
    };
  }

  const primaryBlock = extensions[primaryIndex];
  const exts = primaryBlock.extension ?? [];

  const availabilities = getAvailabilityEntries(primaryBlock);
  const weekSchedule =
    availabilities.length > 0
      ? buildWeekScheduleFromAvailabilities(availabilities)
      : DEFAULT_WEEK_SCHEDULE;

  const durationExt = exts.find(
    ({ url }) => url === FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.DURATION
  );
  const slotDuration =
    durationExt?.valueDuration?.value ?? DEFAULT_SLOT_DURATION;

  return {
    weekSchedule,
    slotDuration,
    buffer: parseBuffer(primaryBlock),
    consultations,
    hoursItem,
    hoursDiffer,
    otherBlockCount,
  };
};

/**
 * Whether `duration` can become a new consultation block. One block per
 * duration, so a duration the form already lists is taken.
 */
export const checkConsultationDuration = (
  duration: number,
  consultations: ConsultationBlock[]
): ConsultationDurationError | undefined => {
  if (
    !Number.isInteger(duration) ||
    duration < MIN_CONSULTATION_DURATION ||
    duration > MAX_CONSULTATION_DURATION
  ) {
    return "out-of-range";
  }

  return consultations.some(
    (consultation) => consultation.duration === duration
  )
    ? "taken"
    : undefined;
};

/**
 * A new block, not stored yet. Slots start every `duration`, and the buffer
 * is off: a consultation never inherits one.
 */
export const createConsultationBlock = (
  duration: number,
  display: string
): ConsultationBlock => ({
  extensionIndex: undefined,
  duration,
  alignmentInterval: duration,
  buffer: { enabled: false, minutes: DEFAULT_BUFFER_MINUTES },
  display,
});

/**
 * Places a new block by ascending duration, as the parse lists it, so the
 * open item stays on it when the save's response is parsed again.
 */
export const insertConsultation = (
  consultations: ConsultationBlock[],
  consultation: ConsultationBlock
): { consultations: ConsultationBlock[]; index: number } => {
  const after = consultations.findIndex(
    ({ duration }) => duration > consultation.duration
  );
  const index = after < 0 ? consultations.length : after;

  return {
    consultations: [
      ...consultations.slice(0, index),
      consultation,
      ...consultations.slice(index),
    ],
    index,
  };
};

/**
 * Every block stores a copy of the shared hours, so the schedule keeps at
 * least one block for them to live in.
 */
export const canRemoveConsultation = ({
  consultations,
  otherBlockCount,
}: Pick<ParsedSchedulingParams, "consultations" | "otherBlockCount">) =>
  consultations.length + otherBlockCount > 1;

const buildAvailabilityExtensions = (
  weekSchedule: WeekSchedule
): Extension[] => {
  const rangeMap = new Map<
    string,
    { days: DayCode[]; start: string; duration: number }
  >();

  for (const day of DAY_CODES.filter(
    (dayCode) => weekSchedule[dayCode].enabled
  )) {
    for (const { start, end } of weekSchedule[day].ranges) {
      const duration =
        parseScheduleTime(end).diff(parseScheduleTime(start), "minute") / 60;
      const key = `${start}_${duration}`;
      const entry = rangeMap.get(key) ?? { days: [], start, duration };
      entry.days.push(day);
      rangeMap.set(key, entry);
    }
  }

  return Array.from(rangeMap.values()).map(({ days, start, duration }) => ({
    url: FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.AVAILABILITY,
    valueTiming: {
      repeat: {
        dayOfWeek: days,
        timeOfDay: [`${start}:00`],
        duration,
        durationUnit: "h" as const,
      },
    },
  }));
};

const buildMinutesExtension = (url: string, minutes: number): Extension => ({
  url,
  valueDuration: { value: minutes, unit: "min" },
});

const buildBufferExtensions = ({ enabled, minutes }: SlotBuffer) =>
  enabled
    ? [
        buildMinutesExtension(
          FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.BUFFER,
          minutes
        ),
      ]
    : [];

const buildOfficeVisitSubExtensions = (
  { slotDuration, buffer }: SchedulingParams,
  sharedHours: Extension[]
): Extension[] => [
  ...sharedHours,
  buildMinutesExtension(
    FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.DURATION,
    slotDuration
  ),
  buildMinutesExtension(
    FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
    slotDuration
  ),
  ...buildBufferExtensions(buffer),
];

const withConsultationDisplay = (
  concept: CodeableConcept,
  code: string,
  display: string
): CodeableConcept => ({
  ...concept,
  coding: concept.coding?.map((coding) =>
    coding.code === code ? { ...coding, display } : coding
  ),
});

const replaceSubExtensions = (
  block: Extension,
  replacedUrls: Set<string>,
  subExtensions: Extension[]
): Extension => ({
  ...block,
  extension: [
    ...(block.extension ?? []).filter(({ url }) => !replacedUrls.has(url)),
    ...subExtensions,
  ],
});

/**
 * Its own alignment and buffer, the shared hours, and the display on its
 * `serviceType` coding. The `code` and `duration` never change.
 */
const buildConsultationBlock = (
  block: Extension,
  code: string,
  consultation: ConsultationBlock,
  sharedHours: Extension[]
): Extension => {
  const display = resolveConsultationDisplay(
    consultation.display,
    consultation.duration
  );
  const withDisplay = (block.extension ?? []).map((subExtension) =>
    subExtension.url ===
      FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.SERVICE_TYPE &&
    subExtension.valueCodeableConcept
      ? {
          ...subExtension,
          valueCodeableConcept: withConsultationDisplay(
            subExtension.valueCodeableConcept,
            code,
            display
          ),
        }
      : subExtension
  );

  return replaceSubExtensions(
    { ...block, extension: withDisplay },
    CONSULTATION_OWNED_SCHEDULING_URLS,
    [
      ...sharedHours,
      buildMinutesExtension(
        FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
        consultation.alignmentInterval
      ),
      ...buildBufferExtensions(consultation.buffer),
    ]
  );
};

/**
 * A block created in the form: the code and display, the shared hours, its
 * duration, alignment and buffer. No `system` on the coding, as AKB writes
 * it.
 */
const buildNewConsultationBlock = (
  consultation: ConsultationBlock,
  sharedHours: Extension[]
): Extension => {
  const { duration, alignmentInterval, buffer, display } = consultation;

  return {
    url: FHIR_EXTENSIONS.SCHEDULING_PARAMETERS,
    extension: [
      {
        url: FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.SERVICE_TYPE,
        valueCodeableConcept: {
          coding: [
            {
              code: toConsultationCode(duration),
              display: resolveConsultationDisplay(display, duration),
            },
          ],
        },
      },
      ...sharedHours,
      buildMinutesExtension(
        FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.DURATION,
        duration
      ),
      buildMinutesExtension(
        FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
        alignmentInterval
      ),
      ...buildBufferExtensions(buffer),
    ],
  };
};

/**
 * The form's stored consultations by the index they were loaded from. The
 * version guard keeps `schedule` the one the form was parsed from, so the
 * index still points at the same block.
 */
const indexStoredConsultations = (consultations: ConsultationBlock[]) =>
  new Map(
    consultations.flatMap((consultation) =>
      isNullable(consultation.extensionIndex)
        ? []
        : [[consultation.extensionIndex, consultation]]
    )
  );

type StoredConsultations = ReturnType<typeof indexStoredConsultations>;

/**
 * Stored consultation blocks the form no longer lists: the user removed
 * them. Ascending.
 */
const findRemovedConsultationIndexes = (
  extensions: Extension[],
  storedConsultations: StoredConsultations
): number[] =>
  extensions.flatMap((block, index) =>
    isSchedulingParameters(block) &&
    findConsultationCoding(block) &&
    !storedConsultations.has(index)
      ? [index]
      : []
  );

/**
 * Removes run after the replaces, which address the indexes as loaded,
 * and from the highest index down, so no removal shifts the next one.
 */
const buildRemoveOperations = (
  arrayPath: string,
  ascendingIndexes: number[]
): PatchOperation[] =>
  [...ascendingIndexes].reverse().map((index) => ({
    op: "remove",
    path: `${arrayPath}/${index}`,
  }));

const buildBlockOperations = (
  schedule: Schedule,
  params: SchedulingParams,
  storedConsultations: StoredConsultations,
  removedIndexes: number[]
): PatchOperation[] => {
  const extensions = schedule.extension ?? [];
  const sharedHours = buildAvailabilityExtensions(params.weekSchedule);
  const officeVisitSubExtensions = buildOfficeVisitSubExtensions(
    params,
    sharedHours
  );
  const createdBlocks = params.consultations
    .filter(({ extensionIndex }) => isNullable(extensionIndex))
    .map((consultation) =>
      buildNewConsultationBlock(consultation, sharedHours)
    );

  /**
   * Consultations created before the first save go in with the
   * office-visit block it adds.
   */
  if (!extensions.some(isSchedulingParameters)) {
    const blocks: Extension[] = [
      {
        url: FHIR_EXTENSIONS.SCHEDULING_PARAMETERS,
        extension: officeVisitSubExtensions,
      },
      ...createdBlocks,
    ];

    return extensions.length
      ? blocks.map<PatchOperation>((value) => ({
          op: "add",
          path: "/extension/-",
          value,
        }))
      : [{ op: "add", path: "/extension", value: blocks }];
  }

  /**
   * On a Working hours schedule no block takes the form's slot duration
   * or buffer.
   */
  const officeVisitIndex =
    params.hoursItem === "office-visit"
      ? findSlotDurationOwnerIndex(extensions)
      : -1;
  const removed = new Set(removedIndexes);

  const buildBlock = (block: Extension, index: number): Extension => {
    if (index === officeVisitIndex) {
      return replaceSubExtensions(
        block,
        UI_OWNED_SCHEDULING_URLS,
        officeVisitSubExtensions
      );
    }

    const consultationCoding = findConsultationCoding(block);
    const consultation = storedConsultations.get(index);

    return consultationCoding && consultation
      ? buildConsultationBlock(
          block,
          consultationCoding.code,
          consultation,
          sharedHours
        )
      : replaceSubExtensions(block, SHARED_HOURS_SCHEDULING_URLS, sharedHours);
  };

  const replaceOperations = extensions.flatMap<PatchOperation>(
    (block, index) =>
      isSchedulingParameters(block) && !removed.has(index)
        ? [
            {
              op: "replace",
              path: `/extension/${index}`,
              value: buildBlock(block, index),
            },
          ]
        : []
  );

  return [
    ...replaceOperations,
    ...buildRemoveOperations("/extension", removedIndexes),
    ...createdBlocks.map<PatchOperation>((value) => ({
      op: "add",
      path: "/extension/-",
      value,
    })),
  ];
};

/**
 * Keeps `Schedule.serviceType` listing each consultation with its display.
 * Entries are matched by code, not position: the two arrays are not
 * guaranteed to line up. A consultation with no entry gets one, as AKB
 * writes it — code and display, no `system`. A removed block's entry goes,
 * unless another block still has its code.
 */
const buildServiceTypeOperations = (
  schedule: Schedule,
  consultations: ConsultationBlock[],
  removedIndexes: number[]
): PatchOperation[] => {
  const serviceTypes = schedule.serviceType ?? [];
  const displaysByCode = consultations.reduce((acc, consultation) => {
    const code = toConsultationCode(consultation.duration);

    return acc.has(code)
      ? acc
      : acc.set(
          code,
          resolveConsultationDisplay(
            consultation.display,
            consultation.duration
          )
        );
  }, new Map<string, string>());
  const matchedCodes = new Set<string>();

  const replaceOperations = serviceTypes.flatMap<PatchOperation>(
    (concept, index) => {
      const code = concept.coding
        ?.map((coding) => coding.code)
        .find((candidate) => !!candidate && displaysByCode.has(candidate));

      if (!code) {
        return [];
      }

      matchedCodes.add(code);

      return [
        {
          op: "replace",
          path: `/serviceType/${index}`,
          value: withConsultationDisplay(
            concept,
            code,
            displaysByCode.get(code)!
          ),
        },
      ];
    }
  );

  const extensions = schedule.extension ?? [];
  const removed = new Set(removedIndexes);
  const removedCodes = new Set(
    removedIndexes.flatMap(
      (index) => findConsultationCoding(extensions[index])?.code ?? []
    )
  );
  /**
   * Codes that still have a block after the save: the form's consultations
   * and every block the save keeps, office-visit and out-of-scope ones
   * included
   */
  const keptCodes = new Set([
    ...displaysByCode.keys(),
    ...extensions.flatMap((block, index) =>
      isSchedulingParameters(block) && !removed.has(index)
        ? getServiceTypeCodings(block).flatMap(({ code }) => code ?? [])
        : []
    ),
  ]);
  /**
   * An entry goes only when every block it names is gone
   */
  const removedEntryIndexes = serviceTypes.flatMap((concept, index) => {
    const codes = (concept.coding ?? []).flatMap(({ code }) => code ?? []);

    return codes.some((code) => removedCodes.has(code)) &&
      !codes.some((code) => keptCodes.has(code))
      ? [index]
      : [];
  });

  const missingEntries: CodeableConcept[] = [...displaysByCode]
    .filter(([code]) => !matchedCodes.has(code))
    .map(([code, display]) => ({ coding: [{ code, display }] }));

  /**
   * FHIR has no empty arrays, so a list that loses every entry goes whole
   */
  if (
    removedEntryIndexes.length &&
    removedEntryIndexes.length === serviceTypes.length &&
    !missingEntries.length
  ) {
    return [{ op: "remove", path: "/serviceType" }];
  }

  const removeOperations = buildRemoveOperations(
    "/serviceType",
    removedEntryIndexes
  );

  if (!missingEntries.length) {
    return [...replaceOperations, ...removeOperations];
  }

  return [
    ...replaceOperations,
    ...removeOperations,
    ...(serviceTypes.length
      ? missingEntries.map<PatchOperation>((value) => ({
          op: "add",
          path: "/serviceType/-",
          value,
        }))
      : [{ op: "add" as const, path: "/serviceType", value: missingEntries }]),
  ];
};

/**
 * A `test` on `/meta/versionId` goes first, with the `versionId` the form
 * was loaded from. Not `schedule.meta.versionId`: `schedule` may be a
 * fresher read, and a test against it always passes. The ops address
 * blocks by index, which is safe only because of this test.
 */
export const buildSchedulePatchOperations = (
  schedule: Schedule,
  params: SchedulingParams,
  versionId: string
): PatchOperation[] => {
  const storedConsultations = indexStoredConsultations(params.consultations);
  const removedIndexes = findRemovedConsultationIndexes(
    schedule.extension ?? [],
    storedConsultations
  );

  return [
    { op: "test", path: "/meta/versionId", value: versionId },
    ...buildBlockOperations(
      schedule,
      params,
      storedConsultations,
      removedIndexes
    ),
    ...buildServiceTypeOperations(
      schedule,
      params.consultations,
      removedIndexes
    ),
  ];
};

export const splitLastRange = (ranges: TimeRange[]): TimeRange[] => {
  const last = ranges.at(-1)!;
  return [...ranges, { ...last }];
};

export const copyRangesToEnabledDays = (
  weekSchedule: WeekSchedule,
  ranges: TimeRange[]
): WeekSchedule => {
  const next = { ...weekSchedule };
  DAY_CODES.forEach((day) => {
    if (next[day].enabled) {
      next[day] = {
        ...next[day],
        ranges: ranges.map((range) => ({ ...range })),
      };
    }
  });
  return next;
};

/**
 * `copyRangesToEnabledDays`, but it switches the off days on too. Separate
 * rather than a flag because the two mean different things to a user: on the
 * availability page a disabled day is a day the practitioner does not work,
 * so copying into it would invent working hours; on a Kora follow-up the
 * common shape is the same window every day of the week — 29 of the 30
 * stored follow-ups carry exactly seven — so reaching it is the whole point
 * of the control.
 */
export const copyRangesToAllDays = (
  weekSchedule: WeekSchedule,
  ranges: TimeRange[]
): WeekSchedule =>
  DAY_CODES.reduce<WeekSchedule>(
    (next, day) => ({
      ...next,
      [day]: { enabled: true, ranges: ranges.map((range) => ({ ...range })) },
    }),
    { ...weekSchedule }
  );

export const hasRangeOverlap = (ranges: TimeRange[]): boolean => {
  const sorted = [...ranges].sort(
    (first, second) =>
      parseScheduleTime(first.start).valueOf() -
      parseScheduleTime(second.start).valueOf()
  );
  return sorted.some(
    (range, i) =>
      i > 0 &&
      parseScheduleTime(range.start).isBefore(
        parseScheduleEndTime(sorted[i - 1].end)
      )
  );
};

type PlacedRange = {
  day: DayCode;
  start: string;
  rangeStart: Dayjs;
  minutes: number;
};

/**
 * Every range of the week the calendar paints, placed on its weekday. A day
 * whose ranges overlap shows nothing, since no bookable hours follow from
 * them. `locale` must be the one the calendar grid was rendered with — see
 * the week start below for why.
 */
const placeWeekRanges = ({
  weekSchedule,
  calendarDate,
  locale,
}: {
  weekSchedule: WeekSchedule;
  calendarDate: Dayjs;
  locale: string;
}): PlacedRange[] => {
  /**
   * Locales disagree on which weekday a week starts, and `@ilamy` sets the
   * *global* dayjs locale in an effect — so a date built before it mounts and
   * one it hands back later can resolve to different weeks. Pinning both to
   * the grid's locale ends that drift (CSI-697).
   */
  const weekStart = calendarDate.locale(locale).startOf("week");
  const weekStartWeekday = weekStart.day();

  return DAY_CODES.filter((day) => {
    const { enabled, ranges } = weekSchedule[day];
    if (!enabled) {
      return false;
    }
    const validRanges = ranges.filter(isValidRange);
    return validRanges.length > 0 && !hasRangeOverlap(validRanges);
  }).flatMap((day) =>
    weekSchedule[day].ranges.filter(isValidRange).map(({ start, end }) => {
      const startD = parseScheduleTime(start);
      /**
       * `DAY_CODE_TO_DAY_NUMBER` counts from Sunday, but the week may start on
       * any weekday — measure from that start, and wrap so every day lands
       * inside the painted week.
       */
      const dayOffset =
        (DAY_CODE_TO_DAY_NUMBER[day] - weekStartWeekday + DAYS_IN_WEEK) %
        DAYS_IN_WEEK;

      return {
        day,
        start,
        rangeStart: weekStart
          .add(dayOffset, "day")
          .hour(startD.hour())
          .minute(startD.minute())
          .second(0),
        minutes: parseScheduleEndTime(end).diff(startD, "minute"),
      };
    })
  );
};

/**
 * The Working hours item's calendar: one event per range, since no block
 * there owns a slot duration to split them by.
 */
export const generateWeekRanges = (
  params: Parameters<typeof placeWeekRanges>[0]
): CalendarEvent[] =>
  placeWeekRanges(params).map(({ day, start, rangeStart, minutes }) => ({
    id: `range-${day}-${start}`,
    title: "",
    start: rangeStart,
    end: rangeStart.add(minutes, "minute"),
    data: { type: "slot", colorClass: "bg-emerald-400" },
  }));

/**
 * A slot starts every `alignmentInterval` and must end inside its range. An
 * interval below the duration makes slots overlap.
 */
export const generateWeekSlots = ({
  weekSchedule,
  slotDuration,
  alignmentInterval,
  buffer,
  calendarDate,
  locale,
}: {
  weekSchedule: WeekSchedule;
  slotDuration: number;
  alignmentInterval: number;
  buffer: SlotBuffer;
  calendarDate: Dayjs;
  locale: string;
}): CalendarEvent[] => {
  const eventDuration = buffer.enabled
    ? Math.max(slotDuration - buffer.minutes, 1)
    : slotDuration;

  return placeWeekRanges({ weekSchedule, calendarDate, locale }).flatMap(
    ({ day, start, rangeStart, minutes }) => {
      const slotCount = Math.max(
        Math.floor((minutes - slotDuration) / alignmentInterval) + 1,
        0
      );

      return Array.from({ length: slotCount }, (_, i) => ({
        id: `slot-${day}-${start}-${i}`,
        title: "",
        start: rangeStart.add(i * alignmentInterval, "minute"),
        end: rangeStart.add(i * alignmentInterval + eventDuration, "minute"),
        data: { type: "slot", colorClass: "bg-emerald-400" },
      }));
    }
  );
};
